/**
 * Modelo canônico de seguros visto pela UI — puro, sem React nem ra-core.
 *
 * A UI lê só as views `seguros_*_v` (migration 161), que juntam o canônico
 * (Azos, MAG e o que vier). Toda linha carrega a
 * coluna `seguradora`; este módulo concentra o vocabulário: quem são as
 * seguradoras, quais status existem e como rotular cada um. Se um status novo
 * aparecer no banco, a UI mostra o código cru em vez de esconder a linha.
 */

export type CodigoSeguradora = "azos" | "mag";
export type FiltroSeguradora = "todas" | CodigoSeguradora;

export type Seguradora = { codigo: string; nome: string };

/** Lista estática; a tela prefere `seguros_seguradoras` ativas quando carrega. */
export const SEGURADORAS: Seguradora[] = [
  { codigo: "azos", nome: "Azos" },
  { codigo: "mag", nome: "MAG" },
];

/**
 * Quem responde de verdade hoje, na jornada de cotação (`seguros/cotacao/`).
 * A MAG cota pelo modelo de testes que ela deu para homologação ("YZ",
 * `mag/cotacao.ts`) e exige nome e CPF — ver `problemasDoPerfil`.
 */
export const SEGURADORAS_DISPONIVEIS: ReadonlySet<CodigoSeguradora> = new Set(["azos", "mag"]);

export const rotuloSeguradora = (codigo: string | null | undefined, lista: Seguradora[] = SEGURADORAS) => {
  if (!codigo) return "—";
  return lista.find((s) => s.codigo === codigo)?.nome ?? codigo.toUpperCase();
};

/** Aceita só o que a URL pode carregar; qualquer outra coisa vira "todas". */
export const normalizarFiltroSeguradora = (valor: string | null | undefined): FiltroSeguradora => {
  if (valor === "azos" || valor === "mag") return valor;
  return "todas";
};

export const filtrarPorSeguradora = <T extends { seguradora: string }>(
  linhas: T[],
  filtro: FiltroSeguradora,
): T[] => (filtro === "todas" ? linhas : linhas.filter((l) => l.seguradora === filtro));

// ---------------------------------------------------------------------------
// Status canônicos. A cor vive em comuns.tsx; aqui só o rótulo e o domínio.
// ---------------------------------------------------------------------------

export type StatusProposta =
  | "rascunho"
  | "em_analise"
  | "pendente"
  | "suspensa"
  | "aguardando_pagamento"
  | "aceita"
  | "recusada"
  | "implantada"
  | "cancelada"
  | "expirada"
  | "desconhecido";

export const STATUS_PROPOSTA_ROTULO: Record<StatusProposta, string> = {
  rascunho: "Rascunho",
  em_analise: "Em análise",
  pendente: "Pendente",
  suspensa: "Suspensa",
  aguardando_pagamento: "Aguardando pagamento",
  aceita: "Aceita",
  recusada: "Recusada",
  implantada: "Implantada",
  cancelada: "Cancelada",
  expirada: "Expirada",
  desconhecido: "Desconhecido",
};

/** O que ainda pode virar apólice. O resto já fechou, num sentido ou noutro. */
export const PROPOSTAS_ABERTAS: ReadonlySet<string> = new Set<StatusProposta>([
  "rascunho",
  "em_analise",
  "pendente",
  "suspensa",
  "aguardando_pagamento",
]);

/** Aceita ou já implantada: a proposta deu certo. */
export const PROPOSTAS_GANHAS: ReadonlySet<string> = new Set<StatusProposta>(["aceita", "implantada"]);

export type StatusApolice = "vigente" | "em_atraso" | "suspensa" | "cancelada";

export const STATUS_APOLICE_ROTULO: Record<StatusApolice, string> = {
  vigente: "Vigente",
  em_atraso: "Em atraso",
  suspensa: "Suspensa",
  cancelada: "Cancelada",
};

export type StatusCobranca = "pendente" | "em_atraso" | "recebida" | "paga" | "cancelada";

export const STATUS_COBRANCA_ROTULO: Record<StatusCobranca, string> = {
  pendente: "Pendente",
  em_atraso: "Em atraso",
  recebida: "Recebida",
  paga: "Paga",
  cancelada: "Cancelada",
};

export type StatusComissao = "pendente" | "paga" | "estornada";

export const STATUS_COMISSAO_ROTULO: Record<StatusComissao, string> = {
  pendente: "Pendente",
  paga: "Paga",
  estornada: "Estornada",
};

// ---------------------------------------------------------------------------
// Comissões. Azos entra como TAKE_RATE (o que a North ganha como plataforma);
// MAG manda os tipos do evento comissao/* em caixa alta.
// ---------------------------------------------------------------------------

const TIPO_COMISSAO: Record<string, string> = {
  TAKE_RATE: "Take rate",
  COMISSAO: "Comissão",
  MANUAL: "Manual",
  ESTORNO: "Estorno",
  CAMPANHA: "Campanha",
  FIDELIDADE: "Fidelidade",
  BOLSA: "Bolsa",
  "PRÓ-LABORE": "Pró-labore",
  "PRO-LABORE": "Pró-labore",
  "BOLSA EDUCACIONAL": "Bolsa educacional",
  "ADIANTAMENTO CARTEIRA": "Adiantamento de carteira",
};

const TIPO_PROVISAO: Record<string, string> = {
  "NÃO INFORMADO": "Não informado",
  "NAO INFORMADO": "Não informado",
  ANGARIACAO: "Angariação",
  CARTEIRA: "Carteira",
  INCENTIVO: "Incentivo",
  "APORTE UNICO": "Aporte único",
  "APORTE ESPORADICO": "Aporte esporádico",
  PORTABILIDADE: "Portabilidade",
};

/** Título curto de um código em caixa alta que não está na tabela. */
const suavizar = (codigo: string) => {
  const limpo = codigo.replace(/_/g, " ").trim().toLowerCase();
  return limpo.charAt(0).toUpperCase() + limpo.slice(1);
};

export const tipoComissaoRotulo = (tipo: string | null | undefined) => {
  if (!tipo) return "—";
  return TIPO_COMISSAO[tipo.toUpperCase()] ?? suavizar(tipo);
};

export const tipoProvisaoRotulo = (tipo: string | null | undefined) => {
  if (!tipo) return "—";
  return TIPO_PROVISAO[tipo.toUpperCase()] ?? suavizar(tipo);
};

/**
 * Comissão da corretora na Azos, estimada a partir da parcela paga que chega
 * no take rate (valor_premio = valor da fatura). Manual de comissionamento
 * 2026: 25% vitalício sobre o prêmio sem IOF (0,38%). Conferido em 22/10/2026
 * contra o portal da corretora, centavo a centavo — inclusive o arredondamento
 * em duas etapas (prêmio líquido, depois a comissão), com meio centavo para o par. Bônus de campanha,
 * estorno e saldo devedor ficam de fora: não vêm na API.
 */
export const IOF_AZOS = 0.0038;
export const COMISSAO_CORRETORA_AZOS = 0.25;
/** Meio centavo vai para o par, como no portal: 126,265 → 126,26 e 5,695 → 5,70. */
const centavos = (v: number) => {
  const x = v * 100;
  const piso = Math.floor(x);
  const resto = x - piso;
  if (Math.abs(resto - 0.5) < 1e-9) return (piso % 2 === 0 ? piso : piso + 1) / 100;
  return Math.round(x) / 100;
};

export const comissaoCorretoraAzos = (valorFatura: number | string | null | undefined) => {
  const fatura = Number(valorFatura || 0);
  return centavos(centavos(fatura / (1 + IOF_AZOS)) * COMISSAO_CORRETORA_AZOS);
};

// ---------------------------------------------------------------------------
// Linhas das views, como a UI as lê. Os tipos gerados do Supabase ainda não
// conhecem `seguros_*`; até regenerar, o contrato mora aqui.
// ---------------------------------------------------------------------------

export type TabelaSeguros =
  | "seguros_propostas_v"
  | "seguros_apolices_v"
  | "seguros_apolice_coberturas_v"
  | "seguros_cobrancas_v"
  | "seguros_comissoes_v"
  | "seguros_corretores_v"
  | "seguros_coberturas_catalogo_v"
  | "seguros_faturas"
  | "seguros_pendencias"
  | "seguros_sinistros"
  // Controle diário da inadimplência (migrations 169 e 170).
  | "seguros_movimento_diario"
  | "seguros_inadimplencia_corretora"
  | "seguros_inadimplencia_diaria"
  // Central de cobrança (migration 188): fotografia por apólice.
  | "seguros_cobranca_apolices"
  | "seguros_cobranca_movimento"
  | "seguros_cobranca_fotografia";

/** Uma linha do movimento diário: o que aconteceu num dia, numa corretora. */
export type MovimentoDiario = LinhaSeguradora & {
  dia: string;
  corretor_documento_digits: string;
  corretor_nome: string | null;
  apolices_pagaram: number;
  clientes_pagaram: number;
  valor_pago: number | string;
  cobrancas_venceram: number;
  apolices_venceram: number;
  valor_vencido: number | string;
  apolices_cancelaram: number;
  premio_cancelado: number | string;
};

/** A foto de hoje de uma corretora parceira. */
export type InadimplenciaCorretora = LinhaSeguradora & {
  corretor_documento_digits: string;
  corretor_nome: string | null;
  corretor_email: string | null;
  corretor_telefone: string | null;
  apolices_em_atraso: number;
  clientes_em_atraso: number;
  cobrancas_vencidas: number;
  valor_em_atraso: number | string;
  premio_em_risco: number | string;
  /** Apólices em atraso cuja parcela o sync ainda não trouxe — o valor é piso. */
  apolices_sem_detalhe: number;
  dias_do_mais_antigo: number | null;
};

/**
 * Um ponto da curva: a fotografia daquele dia.
 *
 * MEDIDA, não reconstruída — a série começa no dia em que a migration 169
 * rodou. Dia ausente é dia sem medição, não dia sem atraso.
 */
export type InadimplenciaDoDia = LinhaSeguradora & {
  dia: string;
  corretor_documento_digits: string;
  apolices_em_atraso: number;
  clientes_em_atraso: number;
  cobrancas_vencidas: number;
  valor_em_atraso: number | string;
  premio_em_risco?: number | string;
  apolices_sem_detalhe?: number;
};

export type LinhaSeguradora = { seguradora: string };

export type PropostaV = LinhaSeguradora & {
  numero: string;
  status: string;
  status_original: string | null;
  parecer: string | null;
  oferta_nome: string | null;
  data_protocolo: string | null;
  data_ultima_alteracao: string | null;
  data_implantacao: string | null;
  premio_mensal: number | null;
  segurado_nome: string | null;
  segurado_cpf_digits: string | null;
  corretor_nome: string | null;
  corretor_documento_digits: string | null;
  url: string | null;
  atualizado_em: string | null;
};

export type ApoliceV = LinhaSeguradora & {
  numero: string;
  numero_proposta: string | null;
  status: string;
  produto_nome: string | null;
  premio_mensal: number | null;
  inicio_vigencia: string | null;
  fim_vigencia: string | null;
  data_cancelamento: string | null;
  motivo_cancelamento: string | null;
  dias_atraso: number;
  faturas_em_atraso: number;
  segurado_nome: string | null;
  segurado_cpf_digits: string | null;
  segurado_email: string | null;
  segurado_telefone: string | null;
  corretor_nome: string | null;
  corretor_documento_digits: string | null;
  url: string | null;
};

export type ApoliceCoberturaV = LinhaSeguradora & {
  numero_apolice: string;
  identificador: string;
  nome: string | null;
  capital: number | null;
};

export type ComissaoV = LinhaSeguradora & {
  numero_apolice: string | null;
  numero_lancamento: string | null;
  numero_proposta: string | null;
  competencia: string;
  tipo_comissao: string | null;
  tipo_provisao: string | null;
  tipo_lancamento: string | null;
  status: string | null;
  percentual: number | null;
  valor_comissao: number | null;
  valor_premio: number | null;
  produtor_documento_digits: string | null;
  produtor_nome: string | null;
  segurado_nome: string | null;
};

export type CorretorV = LinhaSeguradora & {
  documento_digits: string;
  codigo: string | null;
  nome: string | null;
  razao_social: string | null;
  susep: string | null;
  ativo: boolean | null;
  inicio: string | null;
  fim: string | null;
  premio_mensal_total: number | null;
};

export type CoberturaCatalogoV = LinhaSeguradora & {
  codigo: string;
  nome: string | null;
  tipo: string | null;
};

/** Chave do catálogo de coberturas: o mesmo código pode existir nas duas. */
export const chaveCobertura = (seguradora: string, codigo: string) => `${seguradora}:${codigo}`;
